import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, Github, Linkedin, Mail } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import GlitchText from './GlitchText';
import ShaderBackground from './ShaderBackground';

const TITLES = [
  'Machine Learning Researcher',
  'Software Engineer',
  'Data Scientist',
  'Full-Stack Developer',
];

function useTypingEffect(texts, typingSpeed = 80, deletingSpeed = 40, pauseDuration = 2000) {
  const [displayText, setDisplayText] = useState('');
  const [textIndex, setTextIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const tick = useCallback(() => {
    const currentText = texts[textIndex];
    if (!isDeleting) {
      setDisplayText(currentText.substring(0, displayText.length + 1));
      if (displayText.length === currentText.length) {
        setTimeout(() => setIsDeleting(true), pauseDuration);
        return;
      }
    } else {
      setDisplayText(currentText.substring(0, displayText.length - 1));
      if (displayText.length === 0) {
        setIsDeleting(false);
        setTextIndex((prev) => (prev + 1) % texts.length);
      }
    }
  }, [displayText, isDeleting, textIndex, texts, pauseDuration]);

  useEffect(() => {
    const speed = isDeleting ? deletingSpeed : typingSpeed;
    const timer = setTimeout(tick, speed);
    return () => clearTimeout(timer);
  }, [tick, isDeleting, deletingSpeed, typingSpeed]);

  return displayText;
}

function StaticNoise() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      // Draw at 1/3 resolution so pixels are blocky when CSS-scaled up
      canvas.width  = Math.max(1, Math.floor(canvas.offsetWidth  / 3));
      canvas.height = Math.max(1, Math.floor(canvas.offsetHeight / 3));
    };
    resize();
    window.addEventListener('resize', resize);

    let rafId;
    let frame = 0;
    const draw = () => {
      frame++;
      // Redraw every 2 frames (~30 fps flicker) for that film-grain speed
      if (frame % 2 === 0) {
        const { width: w, height: h } = canvas;
        const img = ctx.createImageData(w, h);
        const d = img.data;
        for (let i = 0; i < d.length; i += 4) {
          const v = Math.random() * 255;
          d[i]     = v;
          d[i + 1] = v;
          d[i + 2] = v;
          d[i + 3] = Math.random() * 120; // varying alpha per pixel
        }
        ctx.putImageData(img, 0, 0);
      }
      rafId = requestAnimationFrame(draw);
    };
    rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        width: '100%',
        height: '100%',
        imageRendering: 'pixelated',
        opacity: 0.07,
        pointerEvents: 'none',
      }}
    />
  );
}

function ParticleField() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Each artifact flashes at a position for a few frames, then jumps elsewhere
    const artifacts = Array.from({ length: 10 }, () => ({
      x: Math.random(),
      y: Math.random(),
      size: Math.ceil(Math.random() * 3),
      life: Math.floor(Math.random() * 30),
      maxLife: Math.floor(Math.random() * 20) + 15,
    }));

    let rafId;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const a of artifacts) {
        a.life++;
        if (a.life >= a.maxLife) {
          a.x       = Math.random();
          a.y       = Math.random();
          a.size    = Math.ceil(Math.random() * 3);
          a.life    = 0;
          a.maxLife = Math.floor(Math.random() * 20) + 15;
        }

        // Fade in → peak → fade out over the artifact's lifetime
        const alpha = Math.sin((a.life / a.maxLife) * Math.PI) * 0.8;
        ctx.fillStyle = `rgba(255, 60, 255, ${alpha})`;
        ctx.fillRect(
          Math.round(a.x * canvas.width),
          Math.round(a.y * canvas.height),
          a.size,
          a.size
        );
      }

      rafId = requestAnimationFrame(draw);
    };
    rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
}

export default function Hero() {
  const typedText = useTypingEffect(TITLES);
  const { isCyberpunk } = useTheme();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] });
  const nameY = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const midY = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const slowY = useTransform(scrollYProgress, [0, 1], [0, -25]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section
      ref={sectionRef}
      id="hero"
      className={`relative min-h-screen flex items-center justify-center overflow-hidden transition-colors duration-700 ${
        isCyberpunk ? 'bg-cyber-black' : 'bg-white'
      }`}
    >
      <ShaderBackground />

      {/* Cyberpunk-only effects */}
      <AnimatePresence>
        {isCyberpunk && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <StaticNoise />
            <ParticleField />
            <div className="hex-decoration top-20 left-[10%] opacity-30" />
            <div className="hex-decoration bottom-32 right-[15%] opacity-20" style={{ animationDelay: '-5s', width: 40, height: 40 }} />
            <div className="hex-decoration top-1/3 right-[8%] opacity-15" style={{ animationDelay: '-10s', width: 80, height: 80 }} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gradient overlays */}
      <div className={`absolute inset-0 transition-opacity duration-700 ${
        isCyberpunk ? 'opacity-100' : 'opacity-0'
      } bg-gradient-to-b from-transparent via-transparent to-cyber-black`} />

      {/* Content */}
      <motion.div className="relative z-10 text-center px-6 max-w-4xl" style={{ opacity: contentOpacity }}>
        {/* Terminal intro — slow parallax */}
        <motion.div style={{ y: slowY }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`font-mono text-sm mb-6 tracking-wider transition-colors duration-500 ${
              isCyberpunk ? 'text-cyber-magenta' : 'text-indigo-500'
            }`}
          >
            <span>Hello, I'm</span>
          </motion.div>
        </motion.div>

        {/* Name with GlitchText — fast parallax */}
        <motion.div style={{ y: nameY }}>
          <motion.h1
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="font-display text-5xl sm:text-6xl md:text-8xl font-bold mb-4 tracking-wider"
          >
            <GlitchText
              text="SAMUEL"
              as="span"
              className="block transition-colors duration-500"
              cyberClassName="text-white"
              cleanClassName="text-gray-900"
              decodeSpeed={40}
              jitterInterval={4000}
            />
            <GlitchText
              text="BUCKLER"
              as="span"
              className="block transition-all duration-500"
              cyberClassName="text-cyber-magenta neon-text-subtle"
              cleanClassName="text-indigo-600"
              decodeSpeed={40}
              decodeDelay={650}
              jitterInterval={3500}
            />
          </motion.h1>
        </motion.div>

        {/* Typing subtitle + Tagline — mid parallax */}
        <motion.div style={{ y: midY }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="font-mono text-lg sm:text-xl mb-8 h-8"
          >
            <span className={`transition-colors duration-500 ${
              isCyberpunk ? 'text-cyber-magenta/70' : 'text-indigo-400'
            }`}>
              &gt;{' '}
            </span>
            <span className={`transition-colors duration-500 ${
              isCyberpunk ? 'text-white' : 'text-gray-700'
            }`}>
              {typedText}
            </span>
            <span className={`animate-blink ${isCyberpunk ? 'text-cyber-magenta' : 'text-indigo-600'}`}>|</span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.6 }}
            className={`font-body text-lg mb-10 max-w-2xl mx-auto leading-relaxed transition-colors duration-500 ${
              isCyberpunk ? 'text-cyber-dim/80' : 'text-gray-500'
            }`}
          >
            CS @ CU Boulder | Building at the crossroads of
            <span className={isCyberpunk ? ' text-cyber-magenta' : ' text-indigo-600'}> machine learning</span>,
            <span className={isCyberpunk ? ' text-cyber-purple' : ' text-purple-600'}> space physics</span>, and
            <span className={isCyberpunk ? ' text-cyber-pink' : ' text-pink-600'}> computational biology</span>
          </motion.p>
        </motion.div>

        {/* Social Links — slow parallax */}
        <motion.div style={{ y: slowY }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.6 }}
            className="flex items-center justify-center gap-6"
          >
            {[
              { icon: Github, href: 'https://github.com/sabu6516', label: 'GitHub' },
              { icon: Linkedin, href: 'https://linkedin.com/in/samuel-buckler', label: 'LinkedIn' },
              { icon: Mail, href: 'mailto:sabu6516@colorado.edu', label: 'Email' },
            ].map(({ icon: Icon, href, label }) => (
              <motion.a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.2, y: -3 }}
                whileTap={{ scale: 0.95 }}
                className={`w-12 h-12 flex items-center justify-center border transition-all duration-500 ${
                  isCyberpunk
                    ? 'border-cyber-magenta/30 text-cyber-dim hover:text-cyber-magenta hover:border-cyber-magenta/60 hover:shadow-[0_0_15px_rgba(255,0,255,0.3)]'
                    : 'border-gray-300 text-gray-400 hover:text-indigo-600 hover:border-indigo-400 rounded-lg'
                }`}
              >
                <Icon size={20} />
              </motion.a>
            ))}
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.a
          href="#about"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className={`flex flex-col items-center gap-2 transition-colors ${
            isCyberpunk
              ? 'text-cyber-dim/50 hover:text-cyber-magenta'
              : 'text-gray-300 hover:text-indigo-500'
          }`}
        >
          <span className="font-mono text-xs tracking-widest">SCROLL</span>
          <ChevronDown size={16} />
        </motion.a>
      </motion.div>
    </section>
  );
}
